import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Chip,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Divider,
  Skeleton,
  alpha,
  Stack,
  TextField,
  InputAdornment,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Inventory as InventoryIcon,
  ShoppingBag as ShoppingBagIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Error as ErrorIcon,
  Sort as SortIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Pagination from "./Components/Pagination";
import { getGlobalMarketData, getSingleProduct } from "../../Redux/Reducers/ProductsReducer";
import { useDispatch, useSelector } from "react-redux";
import FilterSidebar from "./Components/FilterSidebar";

// Loading Skeleton Component
const ProductCardSkeleton = () => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={140}
      animation="wave"
      sx={{ flexShrink: 0 }}
    />
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, flexGrow: 1 }}>
      <Skeleton variant="text" width="30%" height={20} />
      <Skeleton variant="text" width="90%" height={28} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="70%" height={16} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="50%" height={24} sx={{ mt: 1 }} />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Skeleton variant="rounded" width={100} height={20} />
      </Stack>
    </CardContent>
    <Divider />
    <CardActions sx={{ p: 1.5 }}>
      <Skeleton variant="rounded" width="100%" height={36} />
    </CardActions>
  </Card>
);

// Product Card Component
const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const variantCount = product.variant_count || 0;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const categoryName = product.categories?.[0]?.name || "";

  const getAttributeValues = () => {
    if (product.attributes && product.attributes.length > 0) {
      const attr = product.attributes[0];
      if (attr.value && Array.isArray(attr.value)) {
        return attr.value.join(', ');
      }
      return attr.value;
    }
    return null;
  };

  const attributeValues = getAttributeValues();

  const handleProductClick = async () => {
    await dispatch(getSingleProduct(product.id));
    navigate(`/product-single-page`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[4]
        },
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onClick={handleProductClick}
    >
      <Box sx={{ position: 'relative', height: 180, width: '100%', bgcolor: '#f5f5f5' }}>
        <CardMedia
          component="img"
          image={product.thumbnail || "https://via.placeholder.com/300x300?text=No+Image"}
          alt={product.title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
        {variantCount > 0 && (
          <Chip
            label={`${variantCount} Variant${variantCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: 22,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.65rem'
              }
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: 1.5, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          {product.brand?.name && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: 'text.primary'
              }}
            >
              {product.brand.name}
            </Typography>
          )}
          {categoryName && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: 'text.secondary'
                }}
              >
                {categoryName}
              </Typography>
            </>
          )}
        </Box>

        <Typography
          variant="body2"
          component="h3"
          sx={{
            fontWeight: 600,
            fontSize: '0.9rem',
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            minHeight: '36px'
          }}
        >
          {product.title}
        </Typography>

        {product.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 1,
              fontSize: '0.7rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {product.description.length > 80 ? `${product.description.substring(0, 80)}...` : product.description}
          </Typography>
        )}

        {attributeValues && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              fontSize: '0.65rem',
              color: 'text.secondary',
              fontWeight: 500
            }}
          >
            {attributeValues}
          </Typography>
        )}

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            color: 'primary.main',
            mb: 0.5
          }}
        >
          {formatPrice(product.min_price)}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: product.total_stock > 10 ? "success.main" : "warning.main",
            fontSize: '0.65rem',
            fontWeight: 500
          }}
        >
          {product.total_stock > 0
            ? `In Stock (${product.total_stock})${variantCount > 0 ? ` • ${variantCount} variant${variantCount !== 1 ? 's' : ''}` : ''}`
            : "Out of Stock"}
        </Typography>
      </CardContent>

      <Divider sx={{ my: 0 }} />

      <CardActions sx={{ p: 1.5, pt: 1, pb: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<ShoppingBagIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.75,
            borderRadius: 1.5
          }}
          disabled={product.total_stock === 0}
        >
          Add to Cart
        </Button>
      </CardActions>
    </Card>
  );
};

const ProductMarketplace = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const itemsPerPage = 20;

  const productsState = useSelector(state => state.products);
  const globalMarketData = productsState?.globalproducts?.products;
  const paginationData = productsState?.globalproducts?.pagination;
  const FilteredProducts = productsState?.globalproducts?.filteroptions;
  const apiError = productsState?.globalproducts?.error;

  console.log("FilteredProducts:", FilteredProducts);
  console.log("Global Market Data:", globalMarketData);
  console.log("Pagination Data:", paginationData);
  console.log("Active Filters:", activeFilters);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (activeFilters.brands?.length > 0) count++;
    if (activeFilters.minPrice > 0 || activeFilters.maxPrice < 100000) count++;
    if (debouncedSearchTerm.trim()) count++;
    if (activeFilters.sortBy !== "price" || activeFilters.sortOrder !== "ASC") count++;
    return count;
  }, [activeFilters, debouncedSearchTerm]);

  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  // Fetch data with correct API parameters
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // Build payload according to API documentation
      const payload = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add search term
      if (debouncedSearchTerm.trim()) {
        payload.search = debouncedSearchTerm.trim();
      }

      // Add brand filter - use first brand only (API might not support multiple)
      if (activeFilters.brands && activeFilters.brands.length > 0) {
        // Take the first brand since API might only support single brand
        payload.brand = activeFilters.brands[0];
      }

      // Add price range filters
      if (activeFilters.minPrice && activeFilters.minPrice > 0) {
        payload.minPrice = activeFilters.minPrice;
      }
      if (activeFilters.maxPrice && activeFilters.maxPrice < 100000) {
        payload.maxPrice = activeFilters.maxPrice;
      }

      // Add sorting
      if (activeFilters.sortBy && activeFilters.sortOrder) {
        payload.sortBy = activeFilters.sortBy;
        payload.sortOrder = activeFilters.sortOrder;
      }
      if (activeFilters.sortOrder) {
        payload.sortOrder = activeFilters.sortOrder;
      }

      console.log("📤 Sending API request with payload:", payload);
      const result = await dispatch(getGlobalMarketData(payload));
      console.log("📥 API Response:", result);
      setIsLoading(false);
    };

    fetchData();
  }, [dispatch, currentPage, debouncedSearchTerm, activeFilters]);

  const handleFilterClick = () => {
    setIsFilterOpen(true);
  };

  const handleApplyFilters = (filters) => {
    console.log("🎯 Applying filters:", filters);

    // Transform filters to match API expectations
    const transformedFilters = {};

    if (filters.brands && filters.brands.length > 0) {
      transformedFilters.brands = filters.brands;
    }

    if (filters.minPrice && filters.minPrice > 0) {
      transformedFilters.minPrice = filters.minPrice;
    }
    if (filters.maxPrice && filters.maxPrice < 100000) {
      transformedFilters.maxPrice = filters.maxPrice;
    }

    // Only add sort if it exists (not null)
    if (filters.sortBy && filters.sortOrder) {
      transformedFilters.sortBy = filters.sortBy;
      transformedFilters.sortOrder = filters.sortOrder;
    }

    setActiveFilters(transformedFilters);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };


  const handleClearFilters = () => {
    console.log("🗑️ Clearing all filters");
    setActiveFilters({});
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    console.log("📄 Changing to page:", page);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    if (value === 'none') {
      // Remove sort
      const newFilters = { ...activeFilters };
      delete newFilters.sortBy;
      delete newFilters.sortOrder;
      setActiveFilters(newFilters);
    } else {
      const [sortBy, sortOrder] = value.split('-');
      setActiveFilters(prev => ({
        ...prev,
        sortBy,
        sortOrder
      }));
    }
    setCurrentPage(1);
  };


  // Get available categories and brands from FilteredProducts
  const availableCategories = useMemo(() => {
    if (FilteredProducts?.categories && Array.isArray(FilteredProducts.categories)) {
      return FilteredProducts.categories;
    }
    return [];
  }, [FilteredProducts]);

  const availableBrands = useMemo(() => {
    if (FilteredProducts?.brands && Array.isArray(FilteredProducts.brands)) {
      return FilteredProducts.brands;
    }
    return [];
  }, [FilteredProducts]);

  const currentProducts = useMemo(() => {
    if (Array.isArray(globalMarketData)) {
      return globalMarketData;
    }
    return [];
  }, [globalMarketData]);

  const displayCount = totalItems;
  const showPagination = totalPages > 1;

  const FilterButton = useCallback(({ onClick, activeFilterCount }) => (
    <Button
      onClick={onClick}
      startIcon={<FilterIcon />}
      variant="outlined"
      size="small"
      sx={{
        textTransform: 'none',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap'
      }}
    >
      Filters
      {activeFilterCount > 0 && (
        <Chip
          label={activeFilterCount}
          size="small"
          color="primary"
          sx={{
            ml: 0.5,
            height: 18,
            minWidth: 18,
            '& .MuiChip-label': {
              px: 0.5,
              fontSize: '0.6rem'
            }
          }}
        />
      )}
    </Button>
  ), []);

  const loadingSkeletons = Array(8).fill(0);

  // Get current sort value for display
  const currentSortValue = `${activeFilters.sortBy || 'price'}-${activeFilters.sortOrder || 'ASC'}`;

  if (apiError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" icon={<ErrorIcon />}>
          Error loading products: {apiError}
        </Alert>
      </Container>
    );
  }

  return (
    <div>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}`,
            p: 2
          }}
        >
          {/* Header with Title */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Global Products Marketplace
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {activeFilterCount > 0 || debouncedSearchTerm ? (
                `Found ${displayCount} product${displayCount !== 1 ? 's' : ''} matching your criteria`
              ) : (
                `Total: ${totalItems} products`
              )}
            </Typography>
          </Box>

          {/* Search, Filter and Sort Row */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            mb: 2,
            flexWrap: 'wrap',
          }}>
            {/* Search Bar */}
            <TextField
              size="small"
              placeholder="Search products by name, description, or brand..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.02),
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleClearSearch}
                      size="small"
                      sx={{ minWidth: 'auto', p: 0 }}
                    >
                      <ClearIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            {/* Sort Dropdown */}
            <FormControl size="small" sx={{ minWidth: '180px' }}>
              <InputLabel id="sort-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SortIcon sx={{ fontSize: '1rem' }} />
                  Sort By
                </Box>
              </InputLabel>
              <Select
                labelId="sort-select-label"
                value={activeFilters.sortBy && activeFilters.sortOrder ? `${activeFilters.sortBy}-${activeFilters.sortOrder}` : 'none'}
                onChange={handleSortChange}
                label="Sort By"
              >
                <MenuItem value="none">None (Default)</MenuItem>
                <MenuItem value="price-ASC">Price: Low to High</MenuItem>
                <MenuItem value="price-DESC">Price: High to Low</MenuItem>
                <MenuItem value="title-ASC">Title: A to Z</MenuItem>
                <MenuItem value="title-DESC">Title: Z to A</MenuItem>
              </Select>
            </FormControl>

            {/* Filter Button */}
            <FilterButton
              onClick={handleFilterClick}
              activeFilterCount={activeFilterCount}
            />
          </Box>

          {/* Active Filters Summary */}
          {activeFilterCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                p: 1,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                borderRadius: 1,
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'primary.main' }}>
                  Active Filters:
                </Typography>
                {debouncedSearchTerm && (
                  <Chip
                    label={`Search: "${debouncedSearchTerm}"`}
                    size="small"
                    onDelete={handleClearSearch}
                    sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                )}
                {activeFilters.brands?.map((brand) => (
                  <Chip
                    key={brand}
                    label={`Brand: ${brand}`}
                    size="small"
                    onDelete={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters.brands;
                      setActiveFilters(newFilters);
                      setCurrentPage(1);
                    }}
                    sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                ))}
                {activeFilters.minPrice > 0 && (
                  <Chip
                    label={`Min Price: ₹${activeFilters.minPrice}`}
                    size="small"
                    onDelete={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters.minPrice;
                      setActiveFilters(newFilters);
                      setCurrentPage(1);
                    }}
                    sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                )}
                {activeFilters.maxPrice < 100000 && (
                  <Chip
                    label={`Max Price: ₹${activeFilters.maxPrice}`}
                    size="small"
                    onDelete={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters.maxPrice;
                      setActiveFilters(newFilters);
                      setCurrentPage(1);
                    }}
                    sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                )}
                {(activeFilters.sortBy && activeFilters.sortOrder) && (
                  <Chip
                    label={`Sort: ${activeFilters.sortBy === 'price' ? 'Price' : 'Title'} ${activeFilters.sortOrder === 'ASC' ? '↑' : '↓'}`}
                    size="small"
                    onDelete={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters.sortBy;
                      delete newFilters.sortOrder;
                      setActiveFilters(newFilters);
                      setCurrentPage(1);
                    }}
                    sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                )}
              </Box>
              <Button
                onClick={handleClearFilters}
                size="small"
                sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto' }}
              >
                Clear All
              </Button>
            </Box>
          )}

          {/* Products Grid */}
          {isLoading ? (
            <Grid container spacing={2}>
              {loadingSkeletons.map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <ProductCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : currentProducts.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {currentProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <ProductCard product={product} />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {showPagination && (
                <Box sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Pagination
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    totalPages={totalPages}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                No products found
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {activeFilterCount > 0 || debouncedSearchTerm ? "Try adjusting your filters or search term" : "No products available"}
              </Typography>
              {(activeFilterCount > 0 || debouncedSearchTerm) && (
                <Button
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          )}
        </Paper>

        {/* Filter Sidebar */}
        <FilterSidebar
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApplyFilters={handleApplyFilters}
          availableCategories={availableCategories}
          availableBrands={availableBrands}
          initialFilters={activeFilters}
        />
      </Container>
    </div>
  );
};

export default ProductMarketplace;